import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { CreditScore, ScoreComponent } from '../../types/creditScore';
import { TrendingUp, TrendingDown, DollarSign, Briefcase, CreditCard, Activity, Home, AlertCircle, CheckCircle, Minus } from 'lucide-react';
interface ScoreBreakdownProps {
  score: CreditScore;
}
const componentIcons = {
  paymentHistory: CreditCard,
  incomeStability: Briefcase,
  debtExposure: DollarSign,
  platformBehavior: Activity,
  stabilityTenure: Home
};
const componentNames = {
  paymentHistory: 'Payment History',
  incomeStability: 'Income Stability',
  debtExposure: 'Debt Exposure',
  platformBehavior: 'Platform Behavior',
  stabilityTenure: 'Stability & Tenure'
};
export function ScoreBreakdown({
  score
}: ScoreBreakdownProps) {
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-830">
      <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-831">Score Breakdown</h3>

      <div className="space-y-6" data-id="element-832">
        {Object.entries(score.components).map(([key, component], index) => {
        const Icon = componentIcons[key as keyof typeof componentIcons];
        const name = componentNames[key as keyof typeof componentNames];
        return <motion.div key={key} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: index * 0.1
        }} className="space-y-3" data-id="element-833">
              {/* Component Header */}
              <div className="flex items-center justify-between" data-id="element-834">
                <div className="flex items-center space-x-3" data-id="element-835">
                  <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-836">
                    <Icon className="h-5 w-5 text-blue-600" data-id="element-837" />
                  </div>
                  <div data-id="element-838">
                    <h4 className="font-semibold text-gray-900" data-id="element-839">{name}</h4>
                    <p className="text-xs text-gray-500" data-id="element-840">
                      Weight: {(component.weight * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-841">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-842">
                    {component.score}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-843">/ 100</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-844">
                <motion.div initial={{
              width: 0
            }} animate={{
              width: `${component.score}%`
            }} transition={{
              duration: 1,
              delay: index * 0.1 + 0.3
            }} className={`h-full rounded-full ${component.score >= 80 ? 'bg-green-500' : component.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`} data-id="element-845" />
              </div>

              {/* Factors */}
              <div className="space-y-2 pl-13" data-id="element-846">
                {component.factors.map((factor, factorIndex) => <div key={factorIndex} className="flex items-start space-x-2 text-sm" data-id="element-847">
                    {factor.impact === 'positive' ? <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" data-id="element-848" /> : factor.impact === 'negative' ? <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" data-id="element-849" /> : <Minus className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-850" />}
                    <div className="flex-1" data-id="element-851">
                      <div className="flex items-center justify-between" data-id="element-852">
                        <span className={`font-medium ${factor.impact === 'positive' ? 'text-green-700' : factor.impact === 'negative' ? 'text-red-700' : 'text-gray-700'}`} data-id="element-853">
                          {factor.name}
                        </span>
                        <span className="text-gray-600 font-semibold" data-id="element-854">
                          {factor.value}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-855">
                        {factor.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </motion.div>;
      })}
      </div>

      {/* Summary */}
      <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-856">
        <div className="bg-blue-50 rounded-lg p-4" data-id="element-857">
          <h4 className="font-semibold text-blue-900 mb-2" data-id="element-858">
            Understanding Your Score
          </h4>
          <p className="text-sm text-blue-800 leading-relaxed" data-id="element-859">
            Your credit score is calculated using a weighted combination of
            these five components. Payment history has the highest impact (40%),
            followed by income stability (25%). Focus on improving the
            components with lower scores for the biggest impact.
          </p>
        </div>
      </div>
    </div>;
}